import React, { useState, useEffect, useRef } from "react";
import SuperAdminLayout from "./SuperAdminLayout";
import axios from "axios";
import { toast } from "sonner";
import { getSAToken } from "@/services/superadminApi";
import {
    SectionHeader,
    CardGlass,
    Badge,
    Button,
    StatCard
} from "@/components/ui/PremiumComponents";
import { Map, Activity, Users, ShoppingBag, Search, Filter } from 'lucide-react';
import { MapContainer, TileLayer, Marker, Popup, Circle } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";

// Fix leaflet default icons
const defaultIcon = new L.Icon({
    iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
    shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
    iconSize: [25, 41], iconAnchor: [12, 41]
});

const driverIcon = new L.Icon({
    iconUrl: "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-blue.png",
    shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png",
    iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34], shadowSize: [41, 41]
});

const jobIcon = new L.Icon({
    iconUrl: "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png",
    shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png",
    iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34], shadowSize: [41, 41]
});

export default function SuperAdminGlobalMap() {
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState({ drivers: [], jobs: [] });
    // Config toggles
    const [layers, setLayers] = useState({
        riders: true,
        jobs: true,
        zones: false,
        heatmap: false
    });

    // Interval Ref
    const intervalRef = useRef(null);

    const BASE_URL = (process.env.REACT_APP_API_URL || "http://localhost:8000").replace(/\/api\/?$/, "");

    useEffect(() => {
        fetchMapSnapshot();
        // Refresh cada 10s
        intervalRef.current = setInterval(fetchMapSnapshot, 10000);
        return () => clearInterval(intervalRef.current);
    }, []);

    const fetchMapSnapshot = async () => {
        try {
            const token = getSAToken();
            const res = await axios.get(`${BASE_URL}/api/superadmin/delivery/map/snapshot`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setData(res.data);
            setLoading(false);
        } catch (error) {
            console.error("Error fetching map snapshot", error);
        }
    };

    const toggleLayer = (layer) => {
        setLayers(prev => ({ ...prev, [layer]: !prev[layer] }));
    };

    return (
        <SuperAdminLayout>
            <SectionHeader
                title="Mapa de Operaciones"
                subtitle="Visualización en tiempo real de la actividad global de Conexo POS."
                actions={
                    <div className="flex gap-2 text-sm font-bold">
                        <Button
                            variant={layers.riders ? "primary" : "secondary"}
                            onClick={() => toggleLayer('riders')}
                            className="px-4 py-2"
                        >
                            <Users size={16} className="mr-2 inline" /> Riders ({data.drivers.length})
                        </Button>
                        <Button
                            variant={layers.jobs ? "primary" : "secondary"}
                            onClick={() => toggleLayer('jobs')}
                            className="px-4 py-2"
                        >
                            <ShoppingBag size={16} className="mr-2 inline" /> Jobs ({data.jobs.length})
                        </Button>
                        <Button
                            variant={layers.zones ? "primary" : "secondary"}
                            onClick={() => toggleLayer('zones')}
                            className="px-4 py-2"
                        >
                            Zonas
                        </Button>
                        <Button
                            variant={layers.heatmap ? "primary" : "secondary"}
                            onClick={() => toggleLayer('heatmap')}
                            className="px-4 py-2"
                        >
                            Heatmap
                        </Button>
                    </div>
                }
            />

            <div className="grid grid-cols-1 xl:grid-cols-4 gap-8 h-[calc(100vh-280px)]">
                {/* Main Map Visualization */}
                <div className="xl:col-span-3 relative h-full">
                    <CardGlass className="h-full p-0 overflow-hidden border-none shadow-2xl">
                        {/* Real Map With React Leaflet */}
                        <div className="absolute inset-0 bg-[#020617] h-full w-full">
                            <MapContainer
                                center={[36.8340, -2.4637]} // Almeria default
                                zoom={13}
                                scrollWheelZoom={true}
                                className="z-0"
                                style={{ height: "100%", width: "100%", minHeight: "500px" }}
                                attributionControl={false}
                            >
                                <TileLayer
                                    className="branded-map-tiles"
                                    url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
                                    attribution=""
                                />

                                <style>{`
                                    .branded-map-tiles {
                                        filter: sepia(100%) hue-rotate(180deg) saturate(300%) brightness(0.8) contrast(1.1);
                                    }
                                `}</style>

                                {/* Riders */}
                                {layers.riders && data.drivers?.filter(d => d.lat && d.lng).map((drv) => (
                                    <Marker key={drv.driver_id} position={[drv.lat, drv.lng]} icon={driverIcon}>
                                        <Popup className="bg-[#020617] text-white p-2 text-xs rounded-xl shadow-2xl border border-white/10">
                                            <div className="font-black text-sm text-primary mb-1">{drv.name} ({drv.scope})</div>
                                            <div>Status: {drv.status}</div>
                                            <div>Completados: {drv.completed_count || 0}</div>
                                        </Popup>
                                    </Marker>
                                ))}

                                {/* Jobs */}
                                {layers.jobs && data.jobs?.filter(j => j.pickup_lat && j.pickup_lng).map((job) => (
                                    <Marker key={job.job_id} position={[job.pickup_lat, job.pickup_lng]} icon={jobIcon}>
                                        <Popup className="bg-[#020617] text-white p-2 text-xs rounded-xl shadow-2xl border border-white/10">
                                            <div className="font-black text-sm text-red-400 mb-1">{job.restaurant_name}</div>
                                            <div>ID: {job.job_id.slice(-6).toUpperCase()}</div>
                                            <div>Status: {job.status}</div>
                                        </Popup>
                                    </Marker>
                                ))}

                                {/* Zonas Simuladas */}
                                {layers.zones && data.jobs?.filter(j => j.pickup_lat && j.pickup_lng).map((job) => (
                                    <Circle
                                        key={`zone-${job.job_id}`}
                                        center={[job.pickup_lat, job.pickup_lng]}
                                        pathOptions={{ fillColor: 'var(--primary)', color: 'var(--primary)', fillOpacity: 0.1 }}
                                        radius={3000} // 3km
                                    />
                                ))}

                            </MapContainer>
                        </div>

                        {/* Overlays removed per request */}
                        <div className="absolute bottom-6 right-6 flex gap-4">
                            <StatCard label="Pedidos/min" value="12" className="min-h-0 py-3 px-6 !bg-black/40 !backdrop-blur-md border-white/5" />
                            <StatCard label="Drivers Online" value="42" className="min-h-0 py-3 px-6 !bg-black/40 !backdrop-blur-md border-white/5" />
                        </div>
                    </CardGlass>
                </div>

                {/* Info Sidebar */}
                <div className="xl:col-span-1 flex flex-col gap-6 overflow-y-auto">
                    <CardGlass title="Resumen de Zona" subtitle="Métricas de las últimas 24 h.">
                        <div className="space-y-6 mt-6">
                            <div className="flex justify-between items-center p-3 bg-white/5 rounded-2xl border border-white/5">
                                <div className="flex gap-3 items-center">
                                    <ShoppingBag size={18} className="text-primary" />
                                    <span className="text-xs font-bold text-white">Pedidos Proceso</span>
                                </div>
                                <span className="text-sm font-black text-white">{data.jobs.length}</span>
                            </div>
                            <div className="flex justify-between items-center p-3 bg-white/5 rounded-2xl border border-white/5">
                                <div className="flex gap-3 items-center">
                                    <Users size={18} className="text-secondary" />
                                    <span className="text-xs font-bold text-white">Drivers Online</span>
                                </div>
                                <span className="text-sm font-black text-white">{data.drivers.length}</span>
                            </div>
                        </div>
                    </CardGlass>

                    <CardGlass title="Actividad en Vivo" subtitle="Últimos movimientos.">
                        <div className="space-y-4 mt-4 overflow-y-auto max-h-[400px]">
                            {data.jobs.slice(0, 15).map((job, i) => (
                                <div key={job.job_id} className="flex gap-3 p-3 transition-colors hover:bg-white/5 rounded-xl group border border-white/5">
                                    <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center text-primary group-hover:scale-110 transition-transform">
                                        <Activity size={14} />
                                    </div>
                                    <div className="flex-1">
                                        <p className="text-[10px] font-bold text-white leading-tight">Job {job.job_id.slice(-6)}: {job.status}</p>
                                        <p className="text-[8px] font-black uppercase mt-0.5" style={{ color: 'var(--text-muted)' }}>Tenant: {job.company_id.slice(-8)}</p>
                                    </div>
                                    <span className="text-[8px] font-medium pt-1 text-success">Activo</span>
                                </div>
                            ))}
                            {data.jobs.length === 0 && (
                                <p className="text-xs text-white/50 text-center py-4">No hay jobs activos</p>
                            )}
                        </div>
                    </CardGlass>
                </div>
            </div >
        </SuperAdminLayout >
    );
}
